import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Eye, EyeOff } from "lucide-react";

export default function Auth({ mode }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const isLogin = mode === "login";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    let result;
    if (isLogin) {
      result = await login(email, password);
    } else {
      if (!name.trim()) { setError("Name is required"); setLoading(false); return; }
      if (password.length < 6) { setError("Password must be at least 6 characters"); setLoading(false); return; }
      result = await register(email, password, name);
    }
    setLoading(false);
    if (result.success) {
      navigate(isLogin ? "/dashboard" : "/onboarding");
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen flex items-center justify-center px-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black tracking-tighter uppercase text-[#F5F5F5] mb-2">
            {isLogin ? "SIGN IN" : "CREATE ACCOUNT"}
          </h1>
          <p className="text-[#A3A3A3] text-sm">
            {isLogin ? "Access your scouting profile" : "Start building your scout-ready profile"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card-dark p-8 space-y-5" data-testid="auth-form">
          {!isLogin && (
            <div>
              <label className="overline block mb-2">FULL NAME</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-dark"
                placeholder="Enter your full name"
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div>
            <label className="overline block mb-2">EMAIL</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-dark"
              placeholder="Enter your email"
              required
              data-testid="auth-email-input"
            />
          </div>
          <div>
            <label className="overline block mb-2">PASSWORD</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-dark pr-10"
                placeholder="Enter your password"
                required
                data-testid="auth-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-[#F5F5F5]"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && (
            <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 px-4 py-2 rounded-sm" data-testid="auth-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-gold w-full disabled:opacity-50"
            data-testid="auth-submit"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                {isLogin ? "SIGNING IN..." : "CREATING ACCOUNT..."}
              </span>
            ) : (
              isLogin ? "SIGN IN" : "CREATE ACCOUNT"
            )}
          </button>

          <div className="text-center text-sm text-[#A3A3A3]">
            {isLogin ? (
              <>No account? <Link to="/register" className="text-[#D4AF37] hover:underline" data-testid="auth-switch">Create one</Link></>
            ) : (
              <>Already have an account? <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="auth-switch">Sign in</Link></>
            )}
          </div>
          {isLogin && (
            <div className="text-center text-sm">
              <Link to="/forgot-password" className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
